import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api.ts';

interface Criterion { id: string; target: 'EXTRA' | 'ORG'; code: string; label: string; description: string | null; weight: number; auto: string | null; active: boolean; used: number }
interface Tag { id: string; target: 'EXTRA' | 'ORG'; code: string; emoji: string | null; label: string; polarity: 'POSITIVE' | 'NEGATIVE'; active: boolean; used: number }

const SIDE = { EXTRA: 'Établissement note l’extra', ORG: 'Extra note l’établissement' } as const;
const ERRORS: Record<string, string> = { ALREADY_EXISTS: 'Ce libellé existe déjà.', INVALID_WEIGHT: 'Le poids doit être entre 0 et 100.', MISSING_FIELDS: 'Le libellé est obligatoire.' };

export function AdminRatings() {
  const [data, setData] = useState<{ criteria: Criterion[]; tags: Tag[] } | null>(null);
  const [side, setSide] = useState<'EXTRA' | 'ORG'>('EXTRA');
  const [error, setError] = useState<string | null>(null);
  const [criterion, setCriterion] = useState({ label: '', description: '', weight: '1' });
  const [tag, setTag] = useState({ label: '', emoji: '', polarity: 'POSITIVE' });

  const load = useCallback(() => {
    api.get<{ criteria: Criterion[]; tags: Tag[] }>('/admin/ratings').then(setData);
  }, []);
  useEffect(load, [load]);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      load();
    } catch (err) {
      setError(err instanceof ApiError ? ERRORS[err.code] ?? 'Action impossible.' : 'Action impossible.');
    }
  }

  if (!data) return <div className="empty-state">Chargement…</div>;
  const criteria = data.criteria.filter((c) => c.target === side);
  const tags = data.tags.filter((t) => t.target === side);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Critères notés de 1 à 5 et tags en un clic, pour chaque sens de notation. La note globale est la moyenne des critères pondérée par leur poids. Les seuils (délai de révélation, avis bas, lissage, ponctualité) se règlent dans « Paramètres ». Un critère ou un tag déjà utilisé se désactive.
      </div>
      {error && <div className="error-banner">{error}</div>}

      <div style={{ display: 'flex', gap: 6 }}>
        {(['EXTRA', 'ORG'] as const).map((s) => (
          <button key={s} className={`admin-nav-link${side === s ? ' active' : ''}`} style={{ border: 'none', cursor: 'pointer' }} onClick={() => setSide(s)}>{SIDE[s]}</button>
        ))}
      </div>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 800 }}>Critères</div>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Critère</th><th>Poids</th><th>Utilisé</th><th>Statut</th><th /></tr></thead>
            <tbody>
              {criteria.map((c) => (
                <tr key={c.id} style={{ opacity: c.active ? 1 : 0.55 }}>
                  <td><div style={{ fontWeight: 700 }}>{c.label}{c.auto && <span className="badge badge-neutral" style={{ marginLeft: 8 }}>calculé (pointage)</span>}</div>{c.description && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{c.description}</div>}</td>
                  <td style={{ width: 90 }}>
                    <input className="input" type="number" min={0} max={100} step="0.5" defaultValue={c.weight} aria-label={`Poids — ${c.label}`} style={{ padding: '6px 8px' }} onBlur={(e) => Number(e.target.value) !== c.weight && run(() => api.patch(`/admin/ratings/criteria/${c.id}`, { weight: Number(e.target.value) }))} />
                  </td>
                  <td>{c.used}</td>
                  <td><span className={`badge ${c.active ? 'badge-available' : 'badge-neutral'}`}>{c.active ? 'Actif' : 'Désactivé'}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { const label = window.prompt('Nouveau libellé', c.label); if (label) run(() => api.patch(`/admin/ratings/criteria/${c.id}`, { label })); }}>Renommer</button>{' '}
                    <button className="btn btn-secondary btn-sm" onClick={() => run(() => api.patch(`/admin/ratings/criteria/${c.id}`, { active: !c.active }))}>{c.active ? 'Désactiver' : 'Réactiver'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form className="card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} onSubmit={(e: FormEvent) => { e.preventDefault(); run(async () => { await api.post('/admin/ratings/criteria', { target: side, label: criterion.label, description: criterion.description || undefined, weight: Number(criterion.weight) }); setCriterion({ label: '', description: '', weight: '1' }); }); }}>
          <input className="input" style={{ flex: 2, minWidth: 180 }} required placeholder="Nouveau critère" value={criterion.label} onChange={(e) => setCriterion({ ...criterion, label: e.target.value })} />
          <input className="input" style={{ flex: 3, minWidth: 180 }} placeholder="Description (facultatif)" value={criterion.description} onChange={(e) => setCriterion({ ...criterion, description: e.target.value })} />
          <input className="input" style={{ width: 90 }} type="number" min={0} max={100} step="0.5" aria-label="Poids" value={criterion.weight} onChange={(e) => setCriterion({ ...criterion, weight: e.target.value })} />
          <button className="btn btn-primary btn-sm">Ajouter</button>
        </form>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 800 }}>Tags</div>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Tag</th><th>Nature</th><th>Utilisé</th><th>Statut</th><th /></tr></thead>
            <tbody>
              {tags.map((t) => (
                <tr key={t.id} style={{ opacity: t.active ? 1 : 0.55 }}>
                  <td style={{ fontWeight: 700 }}>{t.emoji} {t.label}</td>
                  <td><span className={`badge ${t.polarity === 'POSITIVE' ? 'badge-available' : 'badge-pending'}`}>{t.polarity === 'POSITIVE' ? 'Positif (public)' : 'Négatif (privé)'}</span></td>
                  <td>{t.used}</td>
                  <td><span className={`badge ${t.active ? 'badge-available' : 'badge-neutral'}`}>{t.active ? 'Actif' : 'Désactivé'}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { const label = window.prompt('Nouveau libellé', t.label); if (label) run(() => api.patch(`/admin/ratings/tags/${t.id}`, { label })); }}>Renommer</button>{' '}
                    <button className="btn btn-secondary btn-sm" onClick={() => run(() => api.patch(`/admin/ratings/tags/${t.id}`, { active: !t.active }))}>{t.active ? 'Désactiver' : 'Réactiver'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form className="card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} onSubmit={(e: FormEvent) => { e.preventDefault(); run(async () => { await api.post('/admin/ratings/tags', { target: side, label: tag.label, emoji: tag.emoji || undefined, polarity: tag.polarity }); setTag({ label: '', emoji: '', polarity: 'POSITIVE' }); }); }}>
          <input className="input" style={{ width: 70 }} placeholder="😀" aria-label="Emoji" value={tag.emoji} onChange={(e) => setTag({ ...tag, emoji: e.target.value })} />
          <input className="input" style={{ flex: 2, minWidth: 180 }} required placeholder="Nouveau tag" value={tag.label} onChange={(e) => setTag({ ...tag, label: e.target.value })} />
          <select className="input" style={{ width: 'auto' }} aria-label="Nature" value={tag.polarity} onChange={(e) => setTag({ ...tag, polarity: e.target.value })}>
            <option value="POSITIVE">Positif (public)</option>
            <option value="NEGATIVE">Négatif (privé)</option>
          </select>
          <button className="btn btn-primary btn-sm">Ajouter</button>
        </form>
      </section>
    </div>
  );
}
